// Schritt 2: Minimal-Seite für einen task-skopierten externen Gast.
//
// Standalone — keine Sidebar, keine Projekt-Navigation. Zeigt genau EINE
// Aufgabe: Titel, Status, Beschreibung, Termine, Anhänge und den getrennten
// Gast-Kommentar-Faden. Bei permission==="edit" sind Status, Beschreibung,
// Anhang-Upload und Kommentar-Composer aktiv; "read" sieht alles ohne Controls.
//
// Routing-Guard: lädt /api/external/me. Ist der User kein Gast oder
// projekt-skopiert (task_id == null), wird ins Projekt umgeleitet.

import { useEffect, useRef, useState } from "preact/hooks";
import { useLocation } from "wouter-preact";
import { Download, Paperclip, Send, Settings } from "lucide-preact";
import type { GuestComment, GuestTask, TaskStatus } from "@juuplan/shared";
import { TASK_STATUSES } from "@juuplan/shared";
import { api } from "../lib/api";
import { TASK_STATUS_LABEL, TASK_STATUS_VAR, softPillStyle } from "../lib/format";
import { toastError, toastSuccess } from "../lib/toast";
import { LinkifiedText } from "../lib/linkify";

import { Button } from "../components/ui/Button";

interface GuestMe {
  task_id: number | null;
  project_id: number | null;
  permission: "read" | "edit";
  email: string | null;
  name: string | null;
}

interface GuestAttachment {
  id: number;
  filename: string;
  content_type: string;
  size_bytes: number;
  created_at: string;
}

const MAX_BYTES = 10 * 1024 * 1024; // 10 MB

// Must mirror the server's allowlist (apps/server/src/routes/attachments.ts).
const ALLOWED_MIME_PREFIXES = [
  "image/",
  "application/vnd.openxmlformats-officedocument.",
];
const ALLOWED_MIME_EXACT = new Set<string>([
  "application/pdf",
  "text/plain",
  "text/csv",
  "application/zip",
  "application/msword",
]);

function isAllowedMime(t: string): boolean {
  if (ALLOWED_MIME_EXACT.has(t)) return true;
  return ALLOWED_MIME_PREFIXES.some((p) => t.startsWith(p));
}

function prettyBytes(n: number): string {
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
  return `${(n / (1024 * 1024)).toFixed(1)} MB`;
}

function fmtDate(iso: string | null): string {
  if (!iso) return "—";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "—";
  return d.toLocaleDateString("de-DE");
}

function relTime(iso: string): string {
  const t = new Date(iso).getTime();
  if (Number.isNaN(t)) return "";
  const sec = Math.round((Date.now() - t) / 1000);
  if (sec < 60) return "gerade eben";
  const min = Math.round(sec / 60);
  if (min < 60) return `vor ${min} Min.`;
  const hr = Math.round(min / 60);
  if (hr < 24) return `vor ${hr} Std.`;
  const days = Math.round(hr / 24);
  if (days < 7) return `vor ${days} Tag${days === 1 ? "" : "en"}`;
  return new Date(iso).toLocaleDateString("de-DE");
}

/** Read a File as a base64 string (without the data:-prefix). */
function fileToBase64(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
      const result = reader.result;
      if (typeof result !== "string") {
        reject(new Error("Unerwartetes Leseergebnis"));
        return;
      }
      const comma = result.indexOf(",");
      resolve(comma >= 0 ? result.slice(comma + 1) : result);
    };
    reader.onerror = () => reject(reader.error ?? new Error("Lesefehler"));
    reader.readAsDataURL(file);
  });
}

export function GuestTaskView(): preact.JSX.Element {
  const [, setLocation] = useLocation();
  const [task, setTask] = useState<GuestTask | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [description, setDescription] = useState("");
  const [savingDesc, setSavingDesc] = useState(false);
  const [savingStatus, setSavingStatus] = useState(false);

  const [comments, setComments] = useState<GuestComment[]>([]);
  const [commentBody, setCommentBody] = useState("");
  const [postingComment, setPostingComment] = useState(false);

  const [attachments, setAttachments] = useState<GuestAttachment[]>([]);
  const [uploading, setUploading] = useState(false);
  const fileInputRef = useRef<HTMLInputElement | null>(null);

  const canEdit = task?.permission === "edit";

  // Guard + initial load. /api/external/me decides whether this user belongs
  // here at all; a project-scoped guest is redirected into the SPA.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const me = await api<GuestMe>("/api/external/me", { silent401: true });
        if (cancelled) return;
        if (me.task_id == null) {
          // Projekt-skopierter Gast (oder regulärer User) → in die SPA.
          setLocation(me.project_id != null ? `/p/${me.project_id}` : "/login");
          return;
        }
        // Comments/attachments are secondary to the task itself — if either
        // side-fetch fails, show the task with an empty list rather than
        // failing the whole page (the primary `api<GuestTask>` call above is
        // NOT guarded and still surfaces its error below).
        const [t, cs, as] = await Promise.all([
          api<GuestTask>("/api/external/task"),
          api<GuestComment[]>("/api/external/task/comments").catch(() => []),
          api<GuestAttachment[]>("/api/external/task/attachments").catch(() => []),
        ]);
        if (cancelled) return;
        setTask(t);
        setDescription(t.description ?? "");
        setComments(cs);
        setAttachments(as);
        setLoading(false);
      } catch (err) {
        if (cancelled) return;
        if (err && typeof err === "object" && "status" in err && (err as { status: number }).status === 401) {
          setLocation("/login");
          return;
        }
        setError(err instanceof Error ? err.message : "Aufgabe konnte nicht geladen werden.");
        setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const saveStatus = async (next: TaskStatus): Promise<void> => {
    if (!task || next === task.status) return;
    setSavingStatus(true);
    try {
      const updated = await api<GuestTask>("/api/external/task", {
        method: "PATCH",
        body: { status: next },
      });
      setTask(updated);
      toastSuccess("Status aktualisiert.");
    } catch {
      // api.ts toasted
    } finally {
      setSavingStatus(false);
    }
  };

  const saveDescription = async (): Promise<void> => {
    if (!task) return;
    setSavingDesc(true);
    try {
      const updated = await api<GuestTask>("/api/external/task", {
        method: "PATCH",
        body: { description: description || null },
      });
      setTask(updated);
      setDescription(updated.description ?? "");
      toastSuccess("Beschreibung gespeichert.");
    } catch {
      // api.ts toasted
    } finally {
      setSavingDesc(false);
    }
  };

  const postComment = async (): Promise<void> => {
    const trimmed = commentBody.trim();
    if (!trimmed) return;
    setPostingComment(true);
    try {
      const created = await api<GuestComment>("/api/external/task/comments", {
        method: "POST",
        body: { body: trimmed },
      });
      setComments((prev) => [...prev, created]);
      setCommentBody("");
    } catch {
      // api.ts toasted
    } finally {
      setPostingComment(false);
    }
  };

  const uploadFile = async (file: File): Promise<void> => {
    if (file.size > MAX_BYTES) {
      toastError(`Datei zu groß: ${file.name} (${prettyBytes(file.size)}). Maximal 10 MB.`);
      return;
    }
    if (!isAllowedMime(file.type)) {
      toastError(`Dateityp nicht erlaubt: ${file.name} (${file.type || "unbekannt"}).`);
      return;
    }
    setUploading(true);
    try {
      const base64 = await fileToBase64(file);
      const created = await api<GuestAttachment>("/api/external/task/attachments", {
        method: "POST",
        body: { filename: file.name, content_type: file.type || "application/octet-stream", base64 },
      });
      setAttachments((prev) => [...prev, created]);
      toastSuccess(`${file.name} hochgeladen.`);
    } catch {
      // api.ts toasted
    } finally {
      setUploading(false);
    }
  };

  if (loading) {
    return (
      <main class="min-h-dvh flex items-center justify-center p-6 bg-[color:var(--color-surface)]">
        <p class="text-sm text-[color:var(--color-on-surface-variant)]">Aufgabe wird geladen…</p>
      </main>
    );
  }

  if (error || !task) {
    return (
      <main class="min-h-dvh flex items-center justify-center p-6 bg-[color:var(--color-surface)]">
        <div class="max-w-md w-full rounded-lg bg-[color:var(--color-surface-container-lowest)] shadow-level-2 border border-[color:var(--color-outline-variant)] p-6 text-center">
          <h1 class="text-2xl font-bold text-[color:var(--color-on-surface)]">juuplan</h1>
          <p class="mt-3 text-sm text-[color:var(--color-error)]">
            {error ?? "Aufgabe nicht gefunden."}
          </p>
          <p class="mt-3 text-xs text-[color:var(--color-on-surface-variant)]">
            Bitte den Projekt-Verantwortlichen um eine neue Einladung.
          </p>
        </div>
      </main>
    );
  }

  return (
    <main class="min-h-dvh bg-[color:var(--color-surface)] py-8 px-4">
      <div class="mx-auto max-w-2xl">
        <header class="mb-6 flex items-center justify-between gap-3">
          <span class="text-lg font-bold text-[color:var(--color-on-surface)]">juuplan</span>
          <div class="flex items-center gap-2">
            {!canEdit && (
              <span class="inline-flex items-center rounded-full border border-[color:var(--color-outline-variant)] px-2.5 py-0.5 text-xs text-[color:var(--color-on-surface-variant)]">
                Nur-Lese-Zugriff
              </span>
            )}
            <a
              href="/guest-settings"
              class="inline-flex items-center gap-1 rounded border border-[color:var(--color-outline-variant)] px-2 py-1 text-xs text-[color:var(--color-on-surface-variant)] hover:bg-[color:var(--color-surface-container-low)] hover:text-[color:var(--color-on-surface)]"
              title="Einstellungen (Verbinden & Benachrichtigungen)"
            >
              <Settings size={14} aria-hidden="true" />
              Einstellungen
            </a>
          </div>
        </header>

        <div class="rounded-lg border border-[color:var(--color-outline-variant)] bg-[color:var(--color-surface-container-lowest)] shadow-level-1 p-5 sm:p-6 space-y-6">
          {/* Title + status */}
          <div class="flex items-start justify-between gap-3 pb-4 border-b border-[color:var(--color-outline-variant)]">
            <h1 class="text-xl font-semibold text-[color:var(--color-on-surface)] tracking-tight min-w-0 break-words">
              {task.title}
            </h1>
            {canEdit ? (
              <div
                class="juuplan-status-pill relative rounded-md text-sm font-semibold min-h-[32px] shrink-0 hover:brightness-105 cursor-pointer"
                style={softPillStyle(TASK_STATUS_VAR[task.status])}
              >
                <select
                  value={task.status}
                  disabled={savingStatus}
                  onChange={(e) => void saveStatus((e.target as HTMLSelectElement).value as TaskStatus)}
                  class="absolute inset-0 w-full h-full opacity-0 cursor-pointer focus:outline-none"
                  aria-label="Status"
                >
                  {TASK_STATUSES.map((s) => (
                    <option key={s} value={s}>
                      {TASK_STATUS_LABEL[s]}
                    </option>
                  ))}
                </select>
                <span class="flex items-center justify-center w-full h-full pointer-events-none px-3 py-1.5 select-none">
                  {TASK_STATUS_LABEL[task.status]}
                </span>
              </div>
            ) : (
              <span
                class="inline-flex items-center rounded-md px-2.5 py-1 text-xs font-semibold shrink-0"
                style={softPillStyle(TASK_STATUS_VAR[task.status])}
              >
                {TASK_STATUS_LABEL[task.status]}
              </span>
            )}
          </div>

          {/* Description */}
          <section>
            <h2 class="text-xs font-semibold uppercase tracking-wider text-[color:var(--color-on-surface-variant)] mb-2">
              Beschreibung
            </h2>
            {canEdit ? (
              <>
                <textarea
                  value={description}
                  onInput={(e) => setDescription((e.target as HTMLTextAreaElement).value)}
                  rows={6}
                  placeholder="Beschreibung…"
                  class="w-full rounded border border-[color:var(--color-outline-variant)] bg-[color:var(--color-surface-container-lowest)] px-3 py-2 text-base sm:text-sm text-[color:var(--color-on-surface)] focus:outline-none focus-visible:ring-2 focus-visible:ring-[color:var(--color-primary)] focus-visible:ring-offset-2 resize-y"
                  aria-label="Beschreibung"
                />
                <div class="flex items-center justify-end mt-2">
                  <Button
                    variant="primary"
                    size="md"
                    type="button"
                    onClick={() => void saveDescription()}
                    disabled={savingDesc || description === (task.description ?? "")}
                  >
                    {savingDesc ? "Speichere…" : "Beschreibung speichern"}
                  </Button>
                </div>
              </>
            ) : (
              <p class="text-sm text-[color:var(--color-on-surface)] whitespace-pre-wrap min-h-[1.5rem]">
                {(task.description ?? "").trim() || (
                  <span class="italic text-[color:var(--color-on-surface-variant)]">
                    Keine Beschreibung
                  </span>
                )}
              </p>
            )}
          </section>

          {/* Dates (read-only) */}
          <section>
            <h2 class="text-xs font-semibold uppercase tracking-wider text-[color:var(--color-on-surface-variant)] mb-2">
              Termine
            </h2>
            <dl class="grid grid-cols-1 sm:grid-cols-3 gap-3 text-sm">
              <div>
                <dt class="text-label uppercase tracking-wider text-[color:var(--color-on-surface-variant)]">Start</dt>
                <dd class="text-[color:var(--color-on-surface)]">{fmtDate(task.start_date)}</dd>
              </div>
              <div>
                <dt class="text-label uppercase tracking-wider text-[color:var(--color-on-surface-variant)]">Ende</dt>
                <dd class="text-[color:var(--color-on-surface)]">{fmtDate(task.end_date)}</dd>
              </div>
              <div>
                <dt class="text-label uppercase tracking-wider text-[color:var(--color-on-surface-variant)]">Deadline</dt>
                <dd class="text-[color:var(--color-on-surface)]">{fmtDate(task.deadline)}</dd>
              </div>
            </dl>
          </section>

          {/* Attachments */}
          <section>
            <h2 class="text-xs font-semibold uppercase tracking-wider text-[color:var(--color-on-surface-variant)] mb-2">
              Anhänge
            </h2>
            {attachments.length === 0 ? (
              <p class="text-xs text-[color:var(--color-on-surface-variant)] italic">Noch keine Anhänge.</p>
            ) : (
              <ul class="flex flex-col gap-1.5">
                {attachments.map((a) => (
                  <li
                    key={a.id}
                    class="flex items-center gap-3 rounded border border-[color:var(--color-outline-variant)] bg-[color:var(--color-surface-container-low)] px-2.5 py-1.5 text-xs"
                  >
                    <span
                      class="inline-flex items-center justify-center rounded bg-[color:var(--color-surface-container-high)] text-[color:var(--color-on-surface-variant)] shrink-0"
                      style={{ width: "36px", height: "36px" }}
                      aria-hidden="true"
                    >
                      <Paperclip size={16} />
                    </span>
                    <div class="flex-1 min-w-0">
                      <a
                        href={`/api/external/attachments/${a.id}/download`}
                        target="_blank"
                        rel="noopener noreferrer"
                        download={a.filename}
                        class="block truncate font-medium text-[color:var(--color-on-surface)] hover:underline"
                        title={a.filename}
                      >
                        {a.filename}
                      </a>
                      <div class="truncate text-label text-[color:var(--color-on-surface-variant)]">
                        {prettyBytes(a.size_bytes)} · {relTime(a.created_at)}
                      </div>
                    </div>
                    <a
                      href={`/api/external/attachments/${a.id}/download`}
                      target="_blank"
                      rel="noopener noreferrer"
                      download={a.filename}
                      class="rounded p-1 text-[color:var(--color-on-surface-variant)] hover:bg-[color:var(--color-surface-container-high)] transition-colors"
                      aria-label={`${a.filename} herunterladen`}
                    >
                      <Download size={14} aria-hidden="true" />
                    </a>
                  </li>
                ))}
              </ul>
            )}
            {canEdit && (
              <>
                <button
                  type="button"
                  onClick={() => fileInputRef.current?.click()}
                  disabled={uploading}
                  class="mt-2 inline-flex items-center gap-1.5 rounded border border-[color:var(--color-outline-variant)] bg-[color:var(--color-surface-container-lowest)] px-3 py-1.5 text-xs text-[color:var(--color-on-surface)] hover:bg-[color:var(--color-surface-container-low)] disabled:opacity-50"
                >
                  <Paperclip size={14} />
                  {uploading ? "Lädt hoch…" : "Anhang hochladen"}
                </button>
                <input
                  ref={fileInputRef}
                  type="file"
                  class="hidden"
                  accept="image/*,application/pdf,text/plain,text/csv,application/zip,application/msword,.doc,.docx,.xlsx,.pptx,application/vnd.openxmlformats-officedocument.wordprocessingml.document,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet,application/vnd.openxmlformats-officedocument.presentationml.presentation"
                  onChange={(e) => {
                    const input = e.target as HTMLInputElement;
                    const f = input.files?.[0];
                    if (f) void uploadFile(f);
                    input.value = "";
                  }}
                />
                <p class="mt-1 text-label text-[color:var(--color-on-surface-variant)]">
                  Max. 10 MB · Bilder, PDF, Text, ZIP, DOCX, XLSX
                </p>
              </>
            )}
          </section>

          {/* Comment thread */}
          <section>
            <h2 class="text-xs font-semibold uppercase tracking-wider text-[color:var(--color-on-surface-variant)] mb-2">
              Kommentare
            </h2>
            {comments.length === 0 ? (
              <p class="text-xs text-[color:var(--color-on-surface-variant)] italic mb-3">
                Noch keine Kommentare.
              </p>
            ) : (
              <ul class="flex flex-col gap-2 mb-3">
                {comments.map((c) => {
                  const isGuest = c.author === "guest";
                  return (
                    <li
                      key={c.id}
                      class={`flex ${isGuest ? "justify-end" : "justify-start"}`}
                    >
                      <div
                        class={`max-w-[85%] rounded-lg px-3 py-2 text-sm ${
                          isGuest
                            ? "bg-[color:var(--color-primary-container)] text-[color:var(--color-on-primary-container)]"
                            : "bg-[color:var(--color-surface-container-low)] text-[color:var(--color-on-surface)] border border-[color:var(--color-outline-variant)]"
                        }`}
                      >
                        <div class="flex items-center gap-2 mb-0.5">
                          <span class="text-label font-semibold uppercase tracking-wider opacity-80">
                            {isGuest ? "Du" : "Team"}
                          </span>
                          <span class="text-label opacity-70">{relTime(c.created_at)}</span>
                        </div>
                        <div class="whitespace-pre-wrap break-words"><LinkifiedText text={c.body} /></div>
                      </div>
                    </li>
                  );
                })}
              </ul>
            )}

            {canEdit ? (
              <div class="flex items-end gap-2">
                <textarea
                  value={commentBody}
                  onInput={(e) => setCommentBody((e.target as HTMLTextAreaElement).value)}
                  onKeyDown={(e) => {
                    if (e.key === "Enter" && (e.metaKey || e.ctrlKey)) {
                      e.preventDefault();
                      void postComment();
                    }
                  }}
                  rows={2}
                  placeholder="Kommentar schreiben… (Cmd/Strg+Enter sendet)"
                  class="flex-1 rounded border border-[color:var(--color-outline-variant)] bg-[color:var(--color-surface-container-lowest)] px-3 py-2 text-base sm:text-sm text-[color:var(--color-on-surface)] focus:outline-none focus-visible:ring-2 focus-visible:ring-[color:var(--color-primary)] focus-visible:ring-offset-2 resize-y"
                  aria-label="Kommentar schreiben"
                />
                <Button
                  variant="primary"
                  size="md"
                  class="shrink-0"
                  type="button"
                  onClick={() => void postComment()}
                  disabled={!commentBody.trim() || postingComment}
                >
                  <Send size={14} />
                  {postingComment ? "Sende…" : "Senden"}
                </Button>
              </div>
            ) : (
              <p class="text-label text-[color:var(--color-on-surface-variant)] italic">
                Nur-Lese-Zugriff — du kannst keine Kommentare schreiben.
              </p>
            )}
          </section>
        </div>
      </div>
    </main>
  );
}
